import { Link } from "react-router-dom";
import { ArrowUpRight, MapPin, Phone } from "lucide-react";
import { Reveal } from "./motion";
import WhatsAppCTA from "./WhatsAppCTA";

export default function ProjectCard({ project, index = 0 }) {
    return (
        <Reveal delay={(index % 3) * 0.08} className="h-full">
            <article
                data-testid={`project-card-${project.slug}`}
                className="group flex h-full flex-col overflow-hidden rounded-2xl border border-black/8 bg-card transition-all duration-300 hover:-translate-y-1 hover:border-gold/40 hover:shadow-xl hover:shadow-black/5"
            >
                <Link
                    to={`/project/${project.slug}`}
                    data-testid={`project-card-link-${project.slug}`}
                    className="relative block h-56 overflow-hidden"
                >
                    <img
                        src={project.coverImage}
                        alt={project.name}
                        loading="lazy"
                        className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                    />
                    <span className="absolute left-4 top-4 rounded-full bg-obsidian/80 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-gold backdrop-blur">
                        {project.status}
                    </span>
                    {project.typeLabel && (
                        <span className="absolute right-4 top-4 rounded-full bg-obsidian/80 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-ivory backdrop-blur">
                            {project.typeLabel}
                        </span>
                    )}
                </Link>
                <div className="flex flex-1 flex-col p-6">
                    <div className="flex items-start justify-between gap-4">
                        <div>
                            <h3 className="font-serif text-2xl leading-snug text-ivory">
                                <Link
                                    to={`/project/${project.slug}`}
                                    data-testid={`project-card-view-${project.slug}`}
                                    className="transition-colors hover:text-gold"
                                >
                                    {project.name}
                                </Link>
                            </h3>
                            {project.developer?.name && (
                                <p className="mt-1 text-xs text-dim">
                                    by {project.developer.name}
                                </p>
                            )}
                        </div>
                        <Link
                            to={`/project/${project.slug}`}
                            aria-label={`View ${project.name}`}
                            className="mt-1 flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-black/10 text-mist transition-all hover:border-gold hover:text-gold"
                        >
                            <ArrowUpRight className="h-4 w-4" />
                        </Link>
                    </div>
                    <p className="mt-3 inline-flex items-center gap-1.5 text-xs text-mist">
                        <MapPin className="h-3.5 w-3.5 text-gold" />
                        {project.locality}, {project.city}
                    </p>
                    <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 font-mono text-[10px] uppercase tracking-[0.15em] text-dim">
                        {project.typeLabel && <span>{project.typeLabel}</span>}
                        {project.areaLabel && (
                            <>
                                <span className="text-gold">·</span>
                                <span>{project.areaLabel}</span>
                            </>
                        )}
                    </div>
                    <div className="mt-auto pt-5">
                        <div className="flex items-center justify-between border-t border-black/5 pt-4">
                            <p className="text-sm font-medium text-ivory">
                                {project.priceLabel || "Price on request"}
                            </p>
                        </div>
                        <div className="mt-4 flex gap-2">
                            <WhatsAppCTA
                                phone={project.whatsapp}
                                projectName={project.name}
                                label="WhatsApp"
                                testid={`project-card-whatsapp-${project.slug}`}
                                className="flex-1"
                            />
                            {project.phone && (
                                <a
                                    href={`tel:${project.phone}`}
                                    data-testid={`project-card-call-${project.slug}`}
                                    aria-label={`Call about ${project.name}`}
                                    className="inline-flex items-center justify-center gap-2 rounded-full border border-black/12 px-4 py-2.5 text-sm font-medium text-ivory transition-colors hover:border-gold hover:text-gold"
                                >
                                    <Phone className="h-4 w-4" />
                                    Call
                                </a>
                            )}
                        </div>
                    </div>
                </div>
            </article>
        </Reveal>
    );
}
